import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, Workflow, Search, Shield, Loader2 } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdminStats();
  }, []);

  const fetchAdminStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const statsData = await dashboardService.getAdminDashboardStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching admin dashboard stats:', err);
      setError('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  };
  const adminActions = [
    {
      title: 'Customer Search',
      description: 'Search and manage customer licenses',
      href: '/admin/customers',
      icon: Search,
      color: 'bg-blue-500'
    },
    {
      title: 'Template Management',
      description: 'Manage WhatsApp message templates',
      href: '/template-manager',
      icon: FileText,
      color: 'bg-green-500'
    },
    {
      title: 'Flow Management',
      description: 'Create and manage automated workflows',
      href: '/flows',
      icon: Workflow,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="w-full px-4 sm:px-8 pt-8 pb-6">
        <div className="w-full max-w-screen-xl mx-auto space-y-6">
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <Shield className="h-8 w-8 text-gray-600" />
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <p className="text-gray-600">Manage customers, templates, and flows</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
            {adminActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="card w-full hover:shadow-lg transition-shadow group"
              >
                <div className="card-body">
                  <div className="flex items-start space-x-4 w-full">
                    <div className={`h-12 w-12 ${action.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <action.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {action.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Overview</h3>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 w-full">
                    <div className="flex items-center justify-center h-16">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="bg-red-50 rounded-lg p-4 w-full">
                <p className="text-red-600">{error}</p>
              </div>
            ) : stats ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                <div className="bg-blue-50 rounded-lg p-4 w-full">
                  <div className="flex items-start space-x-3">
                    <div className="h-10 w-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Customers</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {new Intl.NumberFormat('en-US').format(stats.totalCustomers)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 w-full">
                  <div className="flex items-start space-x-3">
                    <div className="h-10 w-10 bg-green-500 rounded-lg flex items-center justify-center">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Active Templates</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {new Intl.NumberFormat('en-US').format(stats.activeTemplates)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 w-full">
                  <div className="flex items-start space-x-3">
                    <div className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center">
                      <Workflow className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Active Flows</p>
                      <p className="text-xl font-semibold text-gray-900">
                        {new Intl.NumberFormat('en-US').format(stats.activeFlows)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;





